import React, { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Smartphone, Building2, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { getT } from "@/i18n/translations";


const PAGE_SIZE = 10;

const RedeemCash = () => {
  const t = getT();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: customer, loading } = useSelector((state) => state.customer);

  const [activeTab, setActiveTab] = useState("earned");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loaderRef = useRef(null);

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    if (!customer) dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  const hasBankInfo = customer?.customer?.bankInfo && customer.customer.bankInfo.length > 0;
  const selectedBank = hasBankInfo ? customer.customer.bankInfo.find((b) => b.isDefault) : null;

  const availablePoints =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed === false)
      ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalEarned =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher")
      ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalTransferred =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed === true)
      ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const earnedTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher")
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: item.VoucherUsed,
        date: item.earnedDate,
        slogan: item.remarks,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const transferredTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed)
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: true,
        date: item.updatedDate,
        slogan: item.remarks,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const activeTxs = activeTab === "earned" ? earnedTxs : transferredTxs;
  const visibleTxs = activeTxs.slice(0, visibleCount);
  const hasMore = visibleCount < activeTxs.length;

  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore) {
      setVisibleCount(prev => prev + PAGE_SIZE);
    }
  }, [hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleRedeem = () => {
    if (!availablePoints || availablePoints <= 0) { toast.error(t.redeem_errNoBalance); return; }
    navigate("/bank-details");
  };

  const handleUPIRedeem = () => {
    if (!availablePoints || availablePoints <= 0) { toast.error(t.redeem_errNoBalance); return; }
    navigate("/upi-details", { state: { selectedAmount: availablePoints } });
  };

  return (
    <div
className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
 <div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.redeem_pageTitle}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

    <div className="px-4 pb-8 space-y-3 flex-1">

          {/* ── BALANCE CARD ── */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1">
            <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest text-center mb-1">{t.redeem_balance}</p>
            <p className="text-[48px] font-bold text-[#2d5a2d] text-center leading-tight mb-4">₹{availablePoints}</p>
            <div className="flex justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{t.redeem_totalEarned}</p>
                <p className="text-[15px] font-semibold text-[#1a2e1a]">₹{totalEarned}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{t.redeem_transferred}</p>
                <p className="text-[15px] font-semibold text-[#1a2e1a]">₹{totalTransferred}</p>
              </div>
            </div>
          </div>

          {/* ── TRANSFER FUNDS ── */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1">
            <p className="text-[15px] font-bold text-[#1a2e1a] mb-4">{t.redeem_transferFunds}</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleUPIRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-[1.5px] border-[#e0e8e0] bg-[#f8faf8] active:scale-[0.97] transition-transform hover:border-[#3a6b3a]"
              >
                <div className="w-10 h-10 rounded-xl bg-[#eaf3de] flex items-center justify-center">
                  <Smartphone size={20} className="text-[#2d5a2d]" />
                </div>
                <p className="text-[13px] font-semibold text-[#1a2e1a]">{t.redeem_upiId}</p>
              </button>
              <button
                onClick={handleRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-[1.5px] border-[#e0e8e0] bg-[#f8faf8] active:scale-[0.97] transition-transform hover:border-[#3a6b3a]"
              >
                <div className="w-10 h-10 rounded-xl bg-[#eaf3de] flex items-center justify-center">
                  <Building2 size={20} className="text-[#2d5a2d]" />
                </div>
                <p className="text-[13px] font-semibold text-[#1a2e1a]">{t.redeem_bankAccount}</p>
              </button>
            </div>
          </div>

          {/* ── TABS + TRANSACTION LIST ── */}
          <div className="mx-1 bg-white rounded-2xl shadow-sm overflow-hidden">

            <div className="flex">
              <button
                onClick={() => setActiveTab("earned")}
                className={`flex-1 py-3.5 text-[13px] font-bold transition-all ${activeTab === "earned" ? "text-white" : "text-gray-400 bg-white"}`}
                style={activeTab === "earned" ? { backgroundColor: '#2d5a2d' } : {}}
              >
                {t.redeem_tabEarned} (₹{totalEarned})
              </button>
              <button
                onClick={() => setActiveTab("transferred")}
                className={`flex-1 py-3.5 text-[13px] font-bold transition-all ${activeTab === "transferred" ? "text-white" : "text-gray-400 bg-white"}`}
                style={activeTab === "transferred" ? { backgroundColor: '#2d5a2d' } : {}}
              >
                {t.redeem_tabTransferred} (₹{totalTransferred})
              </button>
            </div>

            <div>
              {visibleTxs.length === 0 && (
                <p className="text-center text-[13px] text-gray-400 py-8">{t.redeem_noTransactions}</p>
              )}

              {visibleTxs.map((tx, i) => {
                const dateObj = new Date(tx.date);
                const date = dateObj.toLocaleDateString();
                return (
                  <div
                    key={tx.id}
                    className={`flex items-center justify-between px-4 py-3.5 ${i !== visibleTxs.length - 1 ? "border-b border-gray-50" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full ${activeTab === "transferred" ? "bg-red-100 text-red-500" : "bg-green-100 text-green-600"}`}>
                        {activeTab === "transferred" ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-[#1a2e1a] leading-tight">{tx.code || "----"}</p>
                        <p className="text-[11px] text-gray-400 leading-tight">{date}</p>
                        {tx.slogan && (
                          <p className="text-[11px] text-[#3a6b3a] font-medium leading-tight mt-0.5 italic">"{tx.slogan}"</p>
                        )}
                      </div>
                    </div>
                    <p className={`text-[14px] font-bold ${activeTab === "transferred" ? "text-red-500" : "text-[#2d5a2d]"}`}>
                      {activeTab === "transferred" ? "-" : "+"}₹{tx.amount}
                    </p>
                  </div>
                );
              })}

              {hasMore && (
                <div ref={loaderRef} className="flex justify-center py-4">
                  <div className="w-5 h-5 border-2 border-[#2d5a2d] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

          </div>

       
        </div>
          <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>
    </div>
  );
};

export default RedeemCash;